import { useId, useState, type FormEvent, type ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { buttonClass } from "@/components/buttonStyles";
import { FIELD_ERROR, FIELD_HELP, FIELD_LABEL } from "@/components/fieldStyles";
import type {
  ActionStanding,
  SignalRequestRefusal,
} from "@/lib/submissions/actionItems";

/**
 * A request the broker sends Signal from a quote or a carrier, which opens an
 * action for Signal's team (PMS-E044): an underwriter review (BRK-S463), a
 * manual carrier's quote (BRK-S395). Its own dialog, as Bond Withdraw's is,
 * with an optional note.
 *
 * Nothing reads as requested until PMS answers: then the dialog says where
 * the request stands, in PMS's answer, and the page reads again behind it.
 * A retry after no answer is the same attempt, so it sends the same
 * Idempotency-Key, as Bind does; the key outlives a closed dialog, since that
 * attempt may have reached Signal. A key PMS has for another request is
 * resent once with a fresh one. The dialog outlives the action that opened
 * it, so the answer stays up once the page, read again, no longer offers it.
 */
export function SignalRequestDialog({
  open,
  onClose,
  title,
  intro,
  noteMax,
  noteHelp,
  send,
  refusalOf,
  answered,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  intro: ReactNode;
  noteMax: number;
  noteHelp: string;
  send: (idempotencyKey: string, note: string) => Promise<ActionStanding>;
  refusalOf: (err: unknown) => SignalRequestRefusal;
  /** PMS's answer, in words: a chip and its line. */
  answered: (standing: ActionStanding) => { chip: string; line: string };
  /** Reads the page again, once PMS has it or it moved on. */
  onChanged: () => void | Promise<void>;
}) {
  const titleId = useId();
  const fieldId = useId();
  const errorId = useId();
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<SignalRequestRefusal | null>(null);
  // This attempt's key, until PMS answers it.
  const [attemptKey, setAttemptKey] = useState<string | null>(null);
  // PMS's answer: the action it opened, or replayed.
  const [standing, setStanding] = useState<ActionStanding | null>(null);

  async function submitWith(key: string, reusedOnce = false): Promise<void> {
    setSending(true);
    setProblem(null);
    setAttemptKey(key);
    try {
      setStanding(await send(key, note));
      setAttemptKey(null);
      void onChanged();
    } catch (err) {
      const refusal = refusalOf(err);
      // The key named another request, so PMS opened nothing for this one:
      // this one is new.
      if (refusal.kind === "key_reused" && !reusedOnce) {
        return submitWith(crypto.randomUUID(), true);
      }
      // No answer: the same attempt, so the same key, on a retry.
      if (refusal.kind !== "network") setAttemptKey(null);
      setProblem(refusal);
      if (refusal.kind === "settled") void onChanged();
    } finally {
      setSending(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (sending) return;
    void submitWith(attemptKey ?? crypto.randomUUID());
  }

  function close() {
    if (!sending) onClose();
  }

  // Asked, or it moved on: the dialog says so, and only closes.
  const said = standing ? answered(standing) : null;
  const finished = said !== null || problem?.kind === "settled";
  return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      {finished ? (
        <>
          <DialogContent>
            <div role="status" className="flex flex-col gap-1 text-sm">
              {said ? (
                <>
                  <p className="font-semibold text-brk-text">{said.chip}</p>
                  <p className="text-brk-text">{said.line}</p>
                </>
              ) : (
                <p className="text-brk-text">{problem?.message}</p>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <button type="button" onClick={close} className={buttonClass()}>
              {said ? "Done" : "Close"}
            </button>
          </DialogActions>
        </>
      ) : (
        <form noValidate onSubmit={submit}>
          <DialogContent>
            <div className="flex flex-col gap-3 text-sm">
              <p className="text-brk-text">{intro}</p>
              <div className="flex flex-col gap-1.5">
                <label htmlFor={fieldId} className={FIELD_LABEL}>
                  Note for Signal (optional)
                </label>
                <textarea
                  id={fieldId}
                  rows={4}
                  value={note}
                  maxLength={noteMax}
                  onChange={(e) => setNote(e.target.value)}
                  aria-invalid={problem ? true : undefined}
                  aria-describedby={problem ? errorId : undefined}
                  className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
                />
                <p className={FIELD_HELP}>{noteHelp}</p>
              </div>
              {problem && (
                <p id={errorId} role="alert" className={FIELD_ERROR}>
                  {problem.message}
                </p>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <button
              type="button"
              onClick={close}
              disabled={sending}
              className={buttonClass({ variant: "secondary" })}
            >
              Not now
            </button>
            <button
              type="submit"
              aria-busy={sending || undefined}
              className={buttonClass()}
            >
              {sending
                ? "Sending…"
                : problem?.kind === "network"
                  ? "Try again"
                  : "Send to Signal"}
            </button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
